// 261010 UI-errbox — MỘT dòng tóm tắt thay hộp đỏ «Còn N mục chưa xong…» ở các bước theo video. THUẦN (không antd).
// Gom theo loại (chưa rõ kết quả · lỗi · bị chặn · chưa chạy), đếm VIDEO và MỤC riêng; lý do bằng tiếng Việt, chữ thô của máy chủ CHỈ ở `raw` (Tooltip).
// Không bao giờ lộ mã ngắn («667480af») hay khoá («continui», «asset-ta»). Mỗi nhóm mang nút máy làm khi đầu mục bước CÓ hành động đó và nút chính chưa nói đúng hành động đó.
import type { StepFlagRow, PrimaryCall, SecondaryAction } from './workflowPrimaryPolicy';
import { callIdentityOf } from './workflowStepHeadPolicy';
import { isTailAssetObject } from './directingTailPolicy';
import type { WorkflowStepKey } from '../../types/filmWorkflow';

export type FailGroupKey = 'UNCERTAIN' | 'FAILED' | 'BLOCKED' | 'NOT_RUN';
export type FailTone = 'bad' | 'warn' | 'todo';
export interface FailEntry { label: string; reason: string; raw: string | null }
export interface FailGroupAction { label: string; hint: string; call: PrimaryCall }
export interface FailGroup {
  key: FailGroupKey; tone: FailTone; icon: 'close' | 'warning' | 'question' | 'lock' | 'clock';
  videos: number; items: number; text: string; entries: FailEntry[]; action: FailGroupAction | null;
}
export interface StepFailSummary { groups: FailGroup[]; text: string; bad: boolean; total: number }

type PrimaryLike = { call: PrimaryCall | null; secondary?: SecondaryAction[] } | null | undefined;

// ── lý do (tiếng Việt) ──
interface Reason { text: string; transient: boolean }
const ENGLISH_JS = /cannot read|undefined|is not a function|is not defined|typeerror|referenceerror|syntaxerror|unexpected token|\bnull\b|stack|at \S+ \(/i;
const NETWORK = /timeout|timed out|etimedout|econn|enotfound|socket hang up|network error|eai_again/i;
const VIET = /[àáảãạăằắẳẵặâầấẩẫậèéẻẽẹêềếểễệìíỉĩịòóỏõọôồốổỗộơờớởỡợùúủũụưừứửữựỳýỷỹỵđ]/i;

/** Mã HTTP trong `error.code` («HTTP_502») hoặc câu «Request failed with status code 502». */
export const httpStatusOf = (code?: string, message?: string): number | null => {
  const m = /^HTTP_([45]\d{2})$/.exec(String(code ?? '')) ?? /status code ([45]\d{2})/i.exec(String(message ?? '')) ?? /\bHTTP[ _]?([45]\d{2})\b/.exec(String(message ?? ''));
  return m ? Number(m[1]) : null;
};

const NOT_RUN_REASON: Record<string, string> = {
  NOT_RUN_BUDGET: 'Chưa chạy vì chạm giới hạn mỗi lượt',
  NOT_RUN_STOP: 'Chưa chạy vì đã bấm Dừng',
  NOT_RUN_INTERRUPTED: 'Chưa chạy vì lượt chạy bị ngắt',
};
const NOT_RUN_SUFFIX: Record<string, string> = { NOT_RUN_BUDGET: 'hết lượt', NOT_RUN_STOP: 'đã dừng', NOT_RUN_INTERRUPTED: 'bị ngắt' };
const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function reasonOf(r: Pick<StepFlagRow, 'outcome' | 'code' | 'rawMessage' | 'outcomeLabel'>): Reason {
  const outcome = String(r.outcome ?? '');
  const msg = String(r.rawMessage ?? '').trim();
  if (NOT_RUN_REASON[outcome]) return { text: NOT_RUN_REASON[outcome], transient: false };
  if (outcome === 'BLOCKED') return { text: cap(r.outcomeLabel), transient: false }; // nhãn nguyên nhân đã là tiếng Việt («chờ bước trước», «chưa có prompt»…)
  const http = httpStatusOf(r.code, msg);
  if (http !== null) {
    if (http >= 500) return { text: 'Lỗi tạm thời của dịch vụ AI', transient: true };
    if (http === 429) return { text: 'Dịch vụ AI đang quá tải — thử lại sau', transient: true };
    if (http === 408) return { text: 'Dịch vụ AI trả lời quá chậm', transient: true };
    return { text: 'Dịch vụ AI từ chối yêu cầu', transient: false };
  }
  if (NETWORK.test(msg) || NETWORK.test(String(r.code ?? ''))) return { text: 'Mất kết nối tới dịch vụ AI', transient: true };
  if (msg && ENGLISH_JS.test(msg)) return { text: 'Lỗi hệ thống khi đọc kết quả', transient: false };
  if (outcome === 'UNCERTAIN') return { text: msg && VIET.test(msg) ? msg : 'Chưa biết đã tạo xong hay chưa — cần đối soát', transient: false };
  if (msg && VIET.test(msg)) return { text: msg.length > 140 ? `${msg.slice(0, 139)}…` : msg, transient: false };
  return { text: 'Lượt chạy gặp lỗi', transient: false };
}

// ── tên hiển thị ──
const PLAN_NAME: Record<string, string> = { continuity: 'Liền mạch cả plan', 'asset-tail': 'Tài sản bổ sung' };
const isVideoRow = (r: StepFlagRow): boolean => r.ownerType === 'event' || /^event:/.test(r.key) || (r.eventIds.length > 0 && r.typeLabel === 'Video');

/** Tên của một mục KHÔNG phải video — không bao giờ là khoá thô. */
export function itemLabelOf(r: Pick<StepFlagRow, 'key' | 'name' | 'typeLabel'>): string {
  const plan = /^plan:(.+)$/.exec(r.key);
  if (plan) return PLAN_NAME[plan[1]] ?? 'Cả plan';
  if (r.name && r.name.trim()) return r.name.trim();
  if (/^kyc:/.test(r.key)) return 'Nhân vật';
  return r.typeLabel || 'Mục';
}

/** [1,2,3,5] → «1–3, 5». */
export function orderRangesOf(orders: number[]): string {
  const s = Array.from(new Set(orders)).sort((a, b) => a - b);
  const out: string[] = [];
  for (let i = 0; i < s.length;) {
    let j = i;
    while (j + 1 < s.length && s[j + 1] === s[j] + 1) j += 1;
    out.push(j > i ? (j === i + 1 ? `${s[i]}, ${s[j]}` : `${s[i]}–${s[j]}`) : String(s[i]));
    i = j + 1;
  }
  return out.join(', ');
}

const ICON: Record<FailGroupKey, FailGroup['icon']> = { UNCERTAIN: 'question', FAILED: 'close', BLOCKED: 'lock', NOT_RUN: 'clock' };
const ORDER: FailGroupKey[] = ['UNCERTAIN', 'FAILED', 'BLOCKED', 'NOT_RUN'];
const groupKeyOf = (outcome: string): FailGroupKey | null =>
  outcome === 'UNCERTAIN' ? 'UNCERTAIN' : outcome === 'FAILED' ? 'FAILED' : outcome === 'BLOCKED' ? 'BLOCKED' : outcome.startsWith('NOT_RUN') ? 'NOT_RUN' : null;

/**
 * Hàng cờ LỖI của một bước → dòng tóm tắt. `rows` = stepFlagRowsOf (đã có outcome/code/rawMessage). `orderOf` = eventId → số video.
 * Nút nhóm: LỖI ⇒ RUN_FAILED; CHƯA CHẠY ⇒ RUN_MISSING (chạy tiếp phần còn thiếu); CHƯA RÕ / BỊ CHẶN ⇒ không có nút máy (đối soát / xử lý bước trước).
 * Nút chính (và link phụ của nó) đã nói đúng hành động ⇒ nhóm chỉ hiện số, không lặp nút.
 */
export function stepFailSummaryOf(
  rows: StepFlagRow[], orderOf: Map<string, number>, step: WorkflowStepKey, actions: SecondaryAction[], primary: PrimaryLike,
): StepFailSummary | null {
  const taken = new Set<string>();
  for (const c of [primary?.call, ...(primary?.secondary ?? []).map((s) => s.call)]) { const id = callIdentityOf(c); if (id) taken.add(id); }
  const actionOf = (key: string, label: string): FailGroupAction | null => {
    const a = actions.find((x) => x.key === key);
    if (!a || !a.enabled || !a.call) return null;
    const id = callIdentityOf(a.call);
    if (id && taken.has(id)) return null;
    return { label, hint: a.label, call: a.call };
  };

  type Acc = { videos: Set<number>; items: Set<string>; entries: Map<string, FailEntry>; transient: boolean[]; suffix: Set<string> };
  const acc = new Map<FailGroupKey, Acc>();
  for (const r of rows) {
    if (r.kind !== 'ERROR') continue;
    if (step === 'DIRECTING' && isTailAssetObject(r.ownerType, r.key)) continue; // «Đạo diễn cần thêm N tài sản» đã nói
    const gk = groupKeyOf(String(r.outcome ?? ''));
    if (!gk) continue;
    const g = acc.get(gk) ?? { videos: new Set<number>(), items: new Set<string>(), entries: new Map<string, FailEntry>(), transient: [], suffix: new Set<string>() };
    acc.set(gk, g);
    const reason = reasonOf(r);
    g.transient.push(reason.transient);
    if (gk === 'NOT_RUN' && NOT_RUN_SUFFIX[String(r.outcome)]) g.suffix.add(NOT_RUN_SUFFIX[String(r.outcome)]);
    const raw = (r.rawMessage || r.code || '').trim() || null;
    let label: string;
    if (isVideoRow(r)) {
      const idFromKey = /^event:(.+)$/.exec(r.key)?.[1];
      const ids = r.eventIds.length ? r.eventIds : idFromKey ? [idFromKey] : [];
      const orders = ids.map((id) => orderOf.get(id)).filter((n): n is number => typeof n === 'number');
      if (orders.length) { orders.forEach((n) => g.videos.add(n)); label = `Video ${orderRangesOf(orders)}`; }
      else { g.items.add(r.key); label = 'Video'; }
    } else { g.items.add(r.key); label = itemLabelOf(r); }
    const ek = `${label}|${reason.text}`;
    if (!g.entries.has(ek)) g.entries.set(ek, { label, reason: reason.text, raw });
  }

  const groups: FailGroup[] = [];
  for (const key of ORDER) {
    const g = acc.get(key);
    if (!g) continue;
    const v = g.videos.size; const it = g.items.size;
    const allTransient = g.transient.length > 0 && g.transient.every(Boolean);
    const what = key === 'UNCERTAIN' ? 'chưa rõ kết quả'
      : key === 'FAILED' ? (allTransient ? 'lỗi tạm thời' : 'lỗi')
      : key === 'BLOCKED' ? 'chưa làm được'
      : `chưa chạy${g.suffix.size === 1 ? ` (${Array.from(g.suffix)[0]})` : ''}`;
    const count = v > 0 && it > 0 ? `${v} video + ${it} mục` : v > 0 ? `${v} video` : `${it} mục`;
    groups.push({
      key, icon: ICON[key], videos: v, items: it, text: `${count} ${what}`, entries: Array.from(g.entries.values()),
      tone: key === 'FAILED' ? (allTransient ? 'warn' : 'bad') : key === 'NOT_RUN' ? 'todo' : 'warn',
      action: key === 'FAILED' ? actionOf('RUN_FAILED', 'Chạy lại') : key === 'NOT_RUN' ? actionOf('RUN_MISSING', 'Chạy tiếp') : null,
    });
  }
  if (groups.length === 0) return null;
  return { groups, text: groups.map((g) => g.text).join(' · '), bad: groups.some((g) => g.tone === 'bad'), total: groups.reduce((n, g) => n + g.videos + g.items, 0) };
}
